import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  OKTA_CONFIG,
  OktaAuthConfigService,
  OktaAuthModule,
} from '@okta/okta-angular';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ToastModule } from 'primeng/toast';
import { FooterComponent } from './footer/footer.component';
import { HeaderComponent } from './header/header.component';
import { UserAccountModule } from '@precisely/prism-ng/user-account';
import { MessageService } from 'primeng/api';
import { MessagesModule } from 'primeng/messages';
import { MessageModule } from 'primeng/message';
import { FooterModule } from '@precisely/prism-ng/footer';
import { SharedModule } from './shared/shared.module';
import {
  INGXLoggerConfig,
  LoggerModule,
  NgxLoggerLevel,
  TOKEN_LOGGER_CONFIG,
} from 'ngx-logger';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslocoRootModule } from './transloco-root.module';
import { DynamicDialogModule } from 'primeng/dynamicdialog';

import { WindowService } from '@precisely/prism-ng/api';
import { IfFeatureModule } from '@precisely/prism-ng/if-feature';
import { ConfigurationService } from './shared/configuration-service/configuration.service';
import { OktaInitializerService } from './authentication/okta-initializer.service';
import { AuthenticationModule } from './authentication/authentication.module';
import { OktaAuth, OktaAuthOptions } from '@okta/okta-auth-js';
import { AuthorizationHttpInterceptor } from './idp-configuration-client/authorization-http.interceptor';
import { APP_WINDOW, AppWindow } from './AppWindow';
import { GlobalErrorHandler } from './core/global-error-handler/global-error-handler';
import { GlobalErrorHandlerModule } from './core/global-error-handler/global-error-handler.module';
import { LogoutComponent } from './logout/logout.component';
import { ErrorDisplayModule } from '@precisely/prism-ng/error-display';
function configInitializer(
  oktaInitializerService: OktaInitializerService,
  configurationService: ConfigurationService,
): () => Promise<void> {
  return async (): Promise<void> => {
    const config = configurationService.getConfiguration();
    const oktaConfig = {
      issuer: config.issuer,
      clientId: config.clientId,
      redirectUri: document.location.origin + '/login/callback',
      scopes: ['openid', 'profile', 'email', 'offline_access'],
    } as OktaAuthOptions;
    const oktaAuth = new OktaAuth(oktaConfig);
    return oktaInitializerService.setOktaConfiguration(oktaAuth);
  };
}

@NgModule({
  declarations: [
    AppComponent,
    FooterComponent,
    HeaderComponent,
    LogoutComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    TranslocoRootModule, // Needed for NGXLogger to get sourcemaps on errors.
    BrowserModule,
    BrowserAnimationsModule,
    DynamicDialogModule,
    AppRoutingModule,
    ToastModule,
    UserAccountModule,
    MessageModule,
    MessagesModule,
    FooterModule,
    SharedModule,
    LoggerModule.forRoot(null),
    GlobalErrorHandlerModule,
    IfFeatureModule,
    AuthenticationModule,
    OktaAuthModule.forRoot(),
    ErrorDisplayModule,
    OktaAuthModule,
  ],
  providers: [
    {
      provide: OKTA_CONFIG,
      useValue: { OktaAuth },
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    MessageService,
    {
      provide: APP_WINDOW,
      useFactory: (window: WindowService) => window.nativeWindow as AppWindow,
      deps: [WindowService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: configInitializer,
      deps: [
        OktaInitializerService,
        ConfigurationService,
        OktaAuthConfigService,
      ],
      multi: true,
    },
    {
      provide: TOKEN_LOGGER_CONFIG,
      useFactory: (configService: ConfigurationService) => {
        return {
          serverLoggingUrl:
            configService.getConfiguration().sumoLoggingSourceEndpoint,
          level: NgxLoggerLevel.DEBUG,
          serverLogLevel: NgxLoggerLevel.INFO,
          enableSourceMaps: true,
        } as INGXLoggerConfig;
      },
      deps: [ConfigurationService],
    },
    LoggerModule,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthorizationHttpInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
